import React, { useState } from "react";
import moment from "moment-timezone";

const DAY_FORMAT = "YYYY-MM-DD";

export type DayMark = "edge" | "range" | null;

interface CalendarProps {
  /** The month to open on (any day in it, YYYY-MM-DD); falls back to `today`. */
  openOn: string;
  today: string;
  /** "edge" fills a day (the date, or a timeline's start and end), "range" tints the days between. */
  markOf: (day: string) => DayMark;
  onPick: (day: string) => void;
}

const NAV_CLASS =
  "flex justify-center items-center w-7 h-7 p-0 bg-transparent rounded border-0 cursor-pointer hover:bg-[#dcdfec]";

const Chevron = ({ flip }: { flip?: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#323338"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d={flip ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6"} />
  </svg>
);

/** A month grid like monday's date picker. Days are plain YYYY-MM-DD strings, with no time zone. */
export const Calendar = ({ openOn, today, markOf, onPick }: CalendarProps) => {
  const [month, setMonth] = useState(() => moment(openOn || today, DAY_FORMAT).startOf("month"));
  const first = month.clone().startOf("week");
  const weeks = Math.ceil((month.day() + month.daysInMonth()) / 7);
  const days = Array.from({ length: weeks * 7 }, (_, i) => first.clone().add(i, "day"));

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex justify-between items-center">
        <button
          type="button"
          aria-label="Previous month"
          className={NAV_CLASS}
          onClick={() => setMonth((m) => m.clone().subtract(1, "month"))}
        >
          <Chevron />
        </button>
        <span className="text-sm font-semibold">{month.format("MMMM YYYY")}</span>
        <button
          type="button"
          aria-label="Next month"
          className={NAV_CLASS}
          onClick={() => setMonth((m) => m.clone().add(1, "month"))}
        >
          <Chevron flip />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 text-center">
        {Array.from({ length: 7 }, (_, i) => first.clone().add(i, "day")).map((day) => (
          <span key={day.day()} className="text-[11px] leading-5 text-light-secondary-text-color">
            {day.format("dd")}
          </span>
        ))}
        {days.map((day) => {
          const key = day.format(DAY_FORMAT);
          const mark = markOf(key);
          const outside = !day.isSame(month, "month");
          const colour =
            mark === "edge"
              ? "bg-[#0073ea] text-white font-semibold hover:bg-[#0060b9]"
              : mark === "range"
                ? "bg-[#cce5ff] text-[#323338] hover:bg-[#aed4fc]"
                : `bg-transparent hover:bg-[#dcdfec] ${outside ? "text-[#a3a6b4]" : "text-[#323338]"}`;
          return (
            <button
              key={key}
              type="button"
              aria-label={day.format("MMMM D, YYYY")}
              aria-pressed={mark !== null}
              className={`h-8 p-0 text-[13px] rounded-full border-0 cursor-pointer !font-figtree ${colour} ${
                key === today && mark !== "edge" ? "ring-1 ring-inset ring-[#0073ea]" : ""
              }`}
              onClick={() => onPick(key)}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SELECT_CLASS =
  "h-[30px] pl-2 pr-6 text-sm bg-white rounded border border-solid border-[#c3c6d4] text-[#323338] appearance-none cursor-pointer focus:border-[#0073ea] focus:outline-none disabled:cursor-default disabled:text-[#a3a6b4] !font-figtree";

/** A select with monday's chevron, spaced off the edge (the browser's arrow sits against it). */
const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <span className="inline-flex relative items-center">
    <select {...props} className={SELECT_CLASS} />
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#676879"
      strokeWidth="2.5"
      aria-hidden="true"
      className="absolute right-2 pointer-events-none"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  </span>
);

const pad = (n: number) => String(n).padStart(2, "0");
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

interface TimeSelectProps {
  /** 24-hour "HH:mm", or "" for no time. */
  value: string;
  onChange: (value: string) => void;
}

/**
 * Hour, minute and AM/PM, the 12-hour form monday shows times in. Replaces the browser's time
 * input, which looks different in every browser and in the monday desktop app.
 */
export const TimeSelect = ({ value, onChange }: TimeSelectProps) => {
  const [h, m] = value ? value.split(":").map(Number) : [NaN, NaN];
  const empty = Number.isNaN(h);
  const pm = !empty && h >= 12;
  const hour12 = empty ? "" : String(h % 12 === 0 ? 12 : h % 12);
  const minutes = empty || MINUTES.includes(m) ? MINUTES : [...MINUTES, m].sort((a, b) => a - b);

  const set = (next12: number, nextMinute: number, nextPm: boolean) =>
    onChange(`${pad((next12 % 12) + (nextPm ? 12 : 0))}:${pad(nextMinute)}`);

  const segment = (on: boolean) =>
    `h-7 px-2.5 text-[13px] border-0 cursor-pointer !font-figtree ${
      on ? "bg-[#0073ea] text-white" : "bg-white text-[#323338] hover:bg-[#dcdfec]"
    }`;

  return (
    <div className="flex gap-1.5 items-center">
      <Select
        aria-label="Hour"
        value={hour12}
        onChange={(event) => set(Number(event.target.value), empty ? 0 : m, pm)}
      >
        {empty && <option value="">--</option>}
        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </Select>
      <span className="text-light-secondary-text-color">:</span>
      <Select
        aria-label="Minute"
        disabled={empty}
        value={empty ? "" : m}
        onChange={(event) => set(Number(hour12), Number(event.target.value), pm)}
      >
        {empty && <option value="">--</option>}
        {minutes.map((n) => (
          <option key={n} value={n}>
            {pad(n)}
          </option>
        ))}
      </Select>
      <div
        role="radiogroup"
        aria-label="AM or PM"
        className="flex overflow-hidden ml-1 rounded border border-solid border-[#c3c6d4]"
      >
        {(["AM", "PM"] as const).map((half) => {
          const on = !empty && pm === (half === "PM");
          return (
            <button
              key={half}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={empty}
              className={segment(on)}
              onClick={() => set(Number(hour12), m, half === "PM")}
            >
              {half}
            </button>
          );
        })}
      </div>
    </div>
  );
};
